import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { formatAccountCurrency } from "../../../../services/util";

export const nonNumberHeaders = [
  "Rate",
  "Account",
  "Reference",
  "Details",
  "Variance",
  "Number",
  "Due Date",
  "VAT #",
  "Type",
  "Transaction",
].reduce((acc, i) => {
  acc[i] = true;
  return acc;
}, {});

export const translatableStrings = [
  "Account",
  "Assets",
  "Balance Sheet",
  "Bank Accounts",
  "Bank",
  "Beginning Credit",
  "Beginning Debit",
  "Cash Received",
  "Cash Spent",
  "Cash",
  "Closing Balance",
  "Credit",
  "Credited",
  "Current Assets",
  "Current Liabilities",
  "Current Year Earnings",
  "Date",
  "Debit",
  "Details",
  "Due Date",
  "Due",
  "Ending Credit",
  "Ending Debit",
  "Equity",
  "Gross Profit",
  "Income",
  "Inventory",
  "Liabilities",
  "Movement Credit",
  "Movement Debit",
  "Net Assets",
  "Net Profit",
  "Net",
  "Number",
  "Opening Balance",
  "Paid",
  "Performance",
  "Position",
  "Profitability",
  "Rate",
  "Reference",
  "Revenue",
  "Tax Basis",
  "Tax",
  "Total Assets",
  "Total Bank",
  "Total Current Assets",
  "Total Current Liabilities",
  "Total Equity",
  "Total Expenses",
  "Total Income",
  "Total Inventory",
  "Total",
  "Transaction",
  "Type",
  "Variance",
  "YTD Credit",
  "YTD Debit",
  "From",
  "To",
  "Tax Period covered",
  "Taxes by Tax Component",
  "Cash received",
  "Cash spent",
  "Cash surplus (deficit)",
  "Closing bank balance",
  "Direct costs",
  "Gross profit (loss)",
  "Other Income",
  "Expenses",
  "Profit (loss)",
  "Debtors",
  "Creditors",
  "Net assets",
  "Number of invoices issued",
  "Average value of invoices",
  "Gross profit margin",
  "Net profit margin",
  "Return on investment (p.a.)",
  "Average debtors days",
  "Average creditors days",
  "Short term cash forecast",
  "Current assets to liabilities",
  "Term assets to liabilities",
  "Source",
  "Description",
  "Reconciled",
  "Amount",
  "Balance"
].reduce((acc, i) => {
  acc[i] = true;
  return acc;
}, {});

export const useProcessReport = () => {
  const { t } = useTranslation();

  const processRow = (row, headers) => {
    let { RowType, Cells, Rows, Title } = row;

    return {
      ...row,
      Title: translatableStrings[Title] ? t(Title) : Title,
      Rows: Rows?.map((row) => processRow(row, headers)),
      Cells: Cells?.map((cell, index) =>
        processCell(cell, index, RowType, headers[index])
      ),
    };
  };

  const processCell = (cell, index, rowType, header) => {
    const headerValue = header?.Value ?? "";
    let valueValue = cell.Value;
    const nonNumberHeader = nonNumberHeaders[headerValue] || !index;

    if (rowType === "Header") {
      return {
        ...cell,
        Value: translatableStrings[headerValue] ? t(headerValue) : headerValue,
        NonNumber: nonNumberHeader,
      };
    } else {
      if (translatableStrings[valueValue]) {
        valueValue = t(valueValue);
      } else {
        const isNumber = v => {
          const type = typeof v;
          if (!(type === 'number' || type === 'string')) return false;
          return !isNaN(Number(v))
        }

        valueValue = isNumber(valueValue)
          ? formatAccountCurrency(valueValue)
          : (valueValue ?? "").toString();
      }

      return {
        ...cell,
        Value: valueValue,
        NonNumber: nonNumberHeader,
      };
    }
  };

  return useCallback(
    (report) => {
      const { Rows, ReportTitles } = report;
      const [header] = Rows;
      const headers = header.Cells;

      if (ReportTitles[0]) {
        ReportTitles[0] = t(ReportTitles[0]);
      }

      return {
        ...report,
        ReportTitles,
        Rows: Rows.map((row) => processRow(row, headers)),
        HeaderFormats: headers.map((i, idx) =>
          nonNumberHeaders[i.Value] || !idx ? "text" : "number"
        ),
      };
    },
    [processRow, t]
  );
};
